import { createContext, useContext, useMemo, type ReactNode } from "react";

import { siteConfig } from "../config";
import type { TrainerPageConfig } from "../types";

/**
 * The facts a public page prints that are not in the calendar: phone, address, opening hours as
 * prose, and the numbers on the home page.
 *
 * On a platform these belong to a trainer, so they arrive with the page at /<slug> and ride in
 * a context rather than being imported. `siteConfig` stays as the fallback, which is what the
 * platform's own pages and any page rendered outside a provider get.
 */
export type SiteConfig = {
  /**
   * The trainer whose page this is, or null on the platform's own pages. Every link in the site
   * chrome is built from it, so one set of sections serves every trainer.
   */
  slug: string | null;
  phone: string;
  /** The same number in the form a `tel:` link wants. */
  phoneHref: string;
  email: string;
  address: string;
  mapUrl: string;
  hours: { weekdays: string; saturday: string | null; sunday: string | null };
  stats: {
    sessionsCoached: string;
    since: string;
    rebookRate: string;
    rating: string;
    reviewCount: string;
    ratingShares: readonly number[];
  };
  calendarDays: number;
};

const FALLBACK: SiteConfig = {
  slug: null,
  phone: siteConfig.phone,
  phoneHref: siteConfig.phoneHref,
  email: siteConfig.email,
  address: siteConfig.address,
  mapUrl: siteConfig.mapUrl,
  hours: { ...siteConfig.hours },
  stats: { ...siteConfig.stats },
  calendarDays: siteConfig.calendarDays,
};

const SiteConfigContext = createContext<SiteConfig>(FALLBACK);

/** `tel:` wants the number with nothing but digits and a leading plus. */
const telHref = (phone: string) => `tel:${phone.replaceAll(/[^+\d]/g, "")}`;

/** Falls back to an OpenStreetMap search when the trainer has not pasted their own map link. */
const mapLink = ({ mapUrl, address }: { mapUrl: string; address: string }) =>
  mapUrl || (address ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(address)}` : "");

/** Turns a trainer's stored page into the shape the template's sections read. */
export const toSiteConfig = (page: TrainerPageConfig): SiteConfig => ({
  slug: page.slug,
  phone: page.phone,
  phoneHref: page.phone ? telHref(page.phone) : "",
  email: page.email,
  address: page.address,
  mapUrl: mapLink(page),
  hours: page.hours,
  stats: page.stats,
  calendarDays: page.calendarDays,
});

export function SiteConfigProvider({ page, children }: { page: TrainerPageConfig | null; children: ReactNode }) {
  const value = useMemo(() => (page ? toSiteConfig(page) : FALLBACK), [page]);
  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export const useSiteConfig = () => useContext(SiteConfigContext);

/**
 * Where a link in the site chrome points. Inside a trainer's page every page of the template
 * lives under their slug; with no slug (the platform's own pages) the paths are the platform's.
 */
export const sitePath = (slug: string | null, page: "" | "services" | "coaches" | "pricing" | "contact" = "") => {
  const suffix = page ? `/${page}` : "";
  return slug ? `/${slug}${suffix}` : `/${page}`;
};

/** One service's page under whichever trainer's site we are on. */
export const servicePath = (slug: string | null, serviceSlug: string) =>
  slug ? `/${slug}/services/${serviceSlug}` : `/services/${serviceSlug}`;
